import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks/reduxHooks';
import { UserRole, Permission } from '../types/auth';
import { useEffect } from 'react';
import { clearAuth } from '../store/slices/authSlice';

// Constants
const SESSION_HEARTBEAT_KEY = 'session_heartbeat';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  requiredPermission?: Permission;  // Optional permission requirement
  bypassAuth?: boolean; // For development purposes only
}

export const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  requiredPermission,
  bypassAuth = false // Default to false for security
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, token } = useAppSelector(state => state.auth);
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Development bypass - Remove in production!
  if (process.env.NODE_ENV === 'development' && bypassAuth) {
    console.log('⚠️ DEVELOPMENT MODE: Bypassing authentication!');
    return <>{children}</>;
  }

  // Check if token exists in localStorage but no user/isAuthenticated in Redux state
  useEffect(() => {
    // This is a data integrity check - token exists but user data is missing
    if (token && (!user || !isAuthenticated)) {
      console.log('⚠️ Token exists but user data is missing - possible session corruption');
      dispatch(clearAuth());  // Clear invalid auth state
    }
    
    // Validate localStorage consistency with Redux state
    const localToken = localStorage.getItem('token');
    const localUser = localStorage.getItem('user');
    
    // Check if localStorage is missing data but Redux has authentication
    if ((!localToken || !localUser) && isAuthenticated) {
      console.log('⚠️ Authentication state inconsistency: Redux authenticated but localStorage empty');
      dispatch(clearAuth());
      return;
    }
    
    // Verify session heartbeat
    if (isAuthenticated) {
      const heartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
      if (!heartbeat) {
        // Reinitialize the heartbeat if missing
        localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
        console.log('✓ Re-established session heartbeat');
      }
    }
  }, [token, user, isAuthenticated, dispatch, location.pathname]);

  // Check if user is authenticated
  if (!isAuthenticated || !token) {
    console.log('❌ User not authenticated, redirecting to login from:', location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has the required role
  if (!user) {
    console.log('❌ User object is missing despite authentication token');
    dispatch(clearAuth()); // Clear corrupted auth state
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user's role is in the allowed roles list
  if (!allowedRoles.includes(user.role)) {
    console.log(`❌ User role ${user.role} not in allowed roles:`, allowedRoles);
    return <Navigate to="/unauthorized" replace />;
  }
  
  // Check if user is suspended
  if (user.suspended) {
    console.log('❌ User account is suspended');
    return <Navigate to="/suspended" replace />;
  }

  // Check for required permissions if specified
  if (requiredPermission && user.dashPermissions) {
    // If a permission is required, check if user has it
    const hasPermission = user.dashPermissions.some(permission => 
      permission.toLowerCase() === requiredPermission.toLowerCase()
    );
    
    if (!hasPermission) {
      console.log(`❌ User lacks required permission: ${requiredPermission}`);
      console.log('Available permissions:', user.dashPermissions);
      return <Navigate to="/unauthorized" replace />;
    }
    
    console.log(`✅ User has required permission: ${requiredPermission}`);
  }

  console.log('✅ User authenticated and authorized for', location.pathname);
  return <>{children}</>;
}; 